// Feature: M004-F0753 (design docs/design/M004-F0753-Bookkeeping-Links.md). The "Money owed" block on the bookkeeping page: income to come
// in and credit owed to customers, as the Ledger summary serves them, each linked to its list when the viewer holds that list's gate.
// Reads only; no new operation. The lists own the PDF download and its checksum rule; this block never opens a PDF.
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { organisationApi } from '../../services/api';
import { invoiceApi } from '../../services/invoiceApi';
import { creditNoteApi } from '../../services/creditNoteApi';
import { money } from '../invoices/invoiceModel';
import { orgListRequest } from './creditNoteModel';
import { creditListIncomplete, listLinks, moneyFigures, reconcile } from './bookkeepingLinksModel';

const PAGE = 200;
const MAX_PAGES = 25;

async function loadInvoices(orgId) {
  const rows = [];
  let offset = 0;
  for (let page = 0; page < MAX_PAGES; page += 1) {
    const data = (await invoiceApi.list(orgId, { limit: PAGE, offset }))?.data?.data;
    if (!data || !Array.isArray(data.invoices)) throw new Error('unreadable invoice list');
    rows.push(...data.invoices);
    if (data.next_offset === null || data.next_offset === undefined) return { rows, complete: true };
    offset = data.next_offset;
  }
  return { rows, complete: false };
}

async function loadCreditNotes(orgId) {
  const data = (await creditNoteApi.list(orgId, orgListRequest('active')))?.data?.data;
  if (!data || !Array.isArray(data.credit_notes)) throw new Error('unreadable credit-note list');
  return { rows: data.credit_notes, complete: !creditListIncomplete(data.credit_notes) };
}

// summaryState = { status: 'loading' | 'ready' | 'error', data, code }. A failed summary shows its code and no figures (never a stale one).
export default function MoneyOwedBlock({ orgId, summaryState }) {
  const { status, data, code } = summaryState;
  const figures = useMemo(() => (status === 'ready' ? moneyFigures(data) : null), [status, data]);
  const [permissions, setPermissions] = useState(null);
  const [lists, setLists] = useState({ invoices: null, creditNotes: null, invoicesComplete: false, creditNotesComplete: false });

  useEffect(() => {
    let current = true;
    setPermissions(null);
    (async () => {
      try {
        const response = await organisationApi.getMyPermissions(orgId);
        const found = (response?.data?.data || response?.data || {}).permissions;
        if (current) setPermissions(found && typeof found === 'object' ? found : {});
      } catch { if (current) setPermissions({}); } // fail closed: no link without a readable projection
    })();
    return () => { current = false; };
  }, [orgId]);

  const links = useMemo(() => listLinks(orgId, permissions || {}), [orgId, permissions]);
  const wantInvoices = Boolean(figures?.available && figures.income !== null && links.invoices);
  const wantCreditNotes = Boolean(figures?.available && figures.credit !== null && links.creditNotes);

  useEffect(() => {
    let current = true;
    setLists({ invoices: null, creditNotes: null, invoicesComplete: false, creditNotesComplete: false });
    if (wantInvoices) {
      loadInvoices(orgId).then((r) => { if (current) setLists((l) => ({ ...l, invoices: r.rows, invoicesComplete: r.complete })); }).catch(() => {});
    }
    if (wantCreditNotes) {
      loadCreditNotes(orgId).then((r) => { if (current) setLists((l) => ({ ...l, creditNotes: r.rows, creditNotesComplete: r.complete })); }).catch(() => {});
    }
    return () => { current = false; };
  }, [orgId, wantInvoices, wantCreditNotes, data]);

  const result = figures?.available ? reconcile(figures, lists) : null;

  return (
    <section className="bk-owed" aria-labelledby="bk-owed-title" data-testid="bk-money-owed">
      <h2 id="bk-owed-title">Money owed</h2>
      {status === 'loading' ? <p role="status" data-testid="bk-owed-loading">Loading money owed…</p> : null}
      {status === 'error' ? (
        <p role="alert" data-testid="bk-owed-error">The Ledger summary is unavailable{code ? ` (${code})` : ''}, so no figures are shown.</p>
      ) : null}
      {status === 'ready' && !figures.available ? (
        <p role="status" data-testid="bk-owed-unavailable">This Ledger does not report money owed yet, so no figures are shown.</p>
      ) : null}
      {figures?.available ? (
        <>
          <div className="bk-owed-figures">
            {figures.income !== null ? (
              <div data-testid="bk-owed-income">
                <span>Income to come in</span>
                <strong>{money(figures.income)}</strong>
                {links.invoices ? <Link to={links.invoices}>View invoices</Link> : null}
              </div>
            ) : null}
            {figures.credit !== null ? (
              <div data-testid="bk-owed-credit">
                <span>Credit owed to customers</span>
                <strong>{money(figures.credit)}</strong>
                {links.creditNotes ? <Link to={links.creditNotes}>View credit notes</Link> : null}
              </div>
            ) : null}
          </div>
          {figures.empty ? <p data-testid="bk-owed-empty">No invoices or credit notes yet</p> : null}
          {result.state === 'match' ? <p role="status" data-testid="bk-owed-match">Matches the invoices and credit notes listed</p> : null}
          {result.state === 'mismatch' ? (
            <p role="alert" data-testid="bk-owed-mismatch">These figures do not match the lists; the Ledger summary is authoritative</p>
          ) : null}
          {result.state === 'not_compared' && permissions !== null ? (
            <p role="status" data-testid="bk-owed-not-compared">Not compared with the invoice and credit-note lists.</p>
          ) : null}
        </>
      ) : null}
    </section>
  );
}
